import React, { useState, useEffect } from 'react';
import { 
  InteractiveBrainGames, 
  ArtisticComingSoon, 
  AITutorPersona, 
  AIFeatureDemos, 
  ProgressGamification 
} from '../components/ai-tutor';

const AITutor: React.FC = () => {
  const [showComingSoon, setShowComingSoon] = useState(true);
  const [currentSection, setCurrentSection] = useState('overview');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const sections = [
    { id: 'overview', name: 'Overview', icon: '🏠' },
    { id: 'persona', name: 'Meet Your AI', icon: '🤖' },
    { id: 'features', name: 'Features', icon: '✨' },
    { id: 'games', name: 'Brain Games', icon: '🎮' },
    { id: 'progress', name: 'Progress', icon: '📊' }
  ];

  const renderSection = () => {
    if (showComingSoon) {
      return <ArtisticComingSoon />;
    }

    switch (currentSection) {
      case 'overview':
        return (
          <div className="overview-section">
            <div className="hero-content">
              <h1 className="hero-title">
                Welcome to Your Personal AI Tutor
              </h1>
              <p className="hero-subtitle">
                Experience the future of personalized learning with cutting-edge AI technology
              </p>
              <div className="hero-features">
                <div className="feature-highlight">
                  <span className="feature-icon">🧠</span>
                  <div>
                    <h3>Adaptive Intelligence</h3>
                    <p>AI that learns your unique learning style and adapts in real-time</p>
                  </div>
                </div>
                <div className="feature-highlight">
                  <span className="feature-icon">🎯</span>
                  <div>
                    <h3>Personalized Learning</h3>
                    <p>Customized study plans and recommendations just for you</p>
                  </div>
                </div>
                <div className="feature-highlight">
                  <span className="feature-icon">🚀</span>
                  <div>
                    <h3>Accelerated Progress</h3>
                    <p>Advanced analytics to maximize your learning efficiency</p>
                  </div>
                </div>
              </div>
              <button 
                className="cta-button"
                onClick={() => setCurrentSection('persona')}
              >
                Meet Your AI Tutor →
              </button>
            </div>
          </div>
        );
      case 'persona':
        return <AITutorPersona />;
      case 'features':
        return <AIFeatureDemos />;
      case 'games':
        return <InteractiveBrainGames />;
      case 'progress':
        return <ProgressGamification />;
      default:
        return <div>Section not found</div>;
    }
  };

  if (!isLoaded) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Initializing AI Tutor...</p>
      </div>
    );
  }

  return (
    <div className="ai-tutor-page">
      <style>{`
        .ai-tutor-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%);
          background-size: 400% 400%;
          animation: gradientShift 15s ease infinite;
          position: relative;
          overflow-x: hidden;
          padding-top: 10px; /* Add padding for fixed navbar */
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .ai-tutor-page::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
          pointer-events: none;
          z-index: 1;
        }

        .page-container {
          position: relative;
          z-index: 2;
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
        }

        .page-header {
          text-align: center;
          margin-bottom: 2rem;
          padding: 2rem 0;
        }

        .page-title {
          font-size: 3rem;
          font-weight: bold;
          color: white;
          margin-bottom: 1rem;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
          background: linear-gradient(45deg, #ff6b9d, #4ecdc4, #45b7d1);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .page-description {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .navigation-tabs {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 3rem;
          flex-wrap: wrap;
          background: rgba(255, 255, 255, 0.1);
          padding: 1rem;
          border-radius: 50px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .nav-tab {
          padding: 0.8rem 1.5rem;
          border: none;
          border-radius: 25px;
          background: transparent;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.95rem;
          font-weight: 500;
        }

        .nav-tab:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }

        .nav-tab.active {
          background: linear-gradient(45deg, #ff6b9d, #4ecdc4);
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
          transform: scale(1.05);
        }

        .content-section {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 20px;
          padding: 2rem;
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          animation: sectionSlideIn 0.6s ease;
        }

        @keyframes sectionSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .overview-section {
          text-align: center;
          color: white;
          padding: 3rem 2rem;
        }

        .hero-title {
          font-size: 2.5rem;
          font-weight: bold;
          margin-bottom: 1rem;
          background: linear-gradient(45deg, #ff6b9d, #4ecdc4, #45b7d1);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          opacity: 0.9;
          margin-bottom: 3rem;
          line-height: 1.6;
        }

        .hero-features {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-bottom: 3rem;
        }

        .feature-highlight {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 15px;
          padding: 2rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          text-align: left;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
        }

        .feature-highlight:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.15);
        }

        .feature-icon {
          font-size: 3rem;
          min-width: 80px;
        }

        .feature-highlight h3 {
          font-size: 1.3rem;
          margin-bottom: 0.5rem;
          color: #4ecdc4;
        }

        .feature-highlight p {
          opacity: 0.9;
          line-height: 1.4;
        }

        .cta-button {
          background: linear-gradient(45deg, #ff6b9d, #4ecdc4);
          border: none;
          padding: 1rem 2rem;
          border-radius: 50px;
          color: white;
          font-size: 1.1rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 10px 20px rgba(0,0,0,0.2);
        }

        .cta-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.3);
        }

        .coming-soon-toggle {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          z-index: 100;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 10px;
          padding: 0.8rem;
          color: white;
          cursor: pointer;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .coming-soon-toggle:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.05);
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .loading-spinner {
          width: 60px;
          height: 60px;
          border: 4px solid rgba(255, 255, 255, 0.3);
          border-left: 4px solid #4ecdc4;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .ai-tutor-page {
            padding-top: 0px; /* Reduced padding for mobile */
          }
          
          .page-container {
            padding: 0rem;
          }
          
          .page-title {
            font-size: 2rem;
          }
          
          .hero-title {
            font-size: 2rem;
          }
          
          .hero-subtitle {
            font-size: 1.1rem;
          }
          
          .navigation-tabs {
            gap: 0.5rem;
            padding: 0.8rem;
          }
          
          .nav-tab {
            padding: 0.6rem 1rem;
            font-size: 0.85rem;
          }
          
          .feature-highlight {
            flex-direction: column;
            text-align: center;
          }
          
          .coming-soon-toggle {
            bottom: 1rem;
            right: 1rem;
          }
          
          .content-section {
            padding: 0rem; /* Reduced padding for mobile */
            margin: 0; /* Remove margin on mobile */
            border:0px;
          }
        }
      `}</style>

      <button 
        className="coming-soon-toggle"
        onClick={() => setShowComingSoon(!showComingSoon)}
        title={showComingSoon ? "Explore Features" : "Show Coming Soon"}
      >
        {showComingSoon ? '👁️ Explore' : '🚧 Coming Soon'}
      </button>

      <div className="page-container">
        {!showComingSoon && (
          <>
            <div className="page-header">
              <h1 className="page-title">🤖 WiziAI Tutor</h1>
              <p className="page-description">
                Your intelligent learning companion powered by advanced AI technology. 
                Discover personalized education that adapts to your unique learning style.
              </p>
            </div>

            <div className="navigation-tabs">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`nav-tab ${currentSection === section.id ? 'active' : ''}`}
                  onClick={() => setCurrentSection(section.id)}
                >
                  <span>{section.icon}</span>
                  <span>{section.name}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="content-section">
          {renderSection()}
        </div>
      </div>
    </div>
  );
};

export default AITutor;